import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Phone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface NavItem {
  name: string;
  path: string;
  external?: boolean;
}

interface NavSection {
  section: string;
  items: NavItem[];
}

interface MobileNavigationProps {
  isOpen: boolean;
  navItems: NavItem[] | NavSection[];
  currentPath: string;
  isPublicSite: boolean;
  onClose: () => void;
  onGetQuote: () => void;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  navItems,
  currentPath,
  isPublicSite,
  onClose,
  onGetQuote
}) => {
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 xl:hidden">
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div id="mobile-menu" className="fixed top-20 lg:top-24 left-0 right-0 bottom-0 bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-200/50 overflow-y-auto">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex flex-col space-y-2">
            {Array.isArray(navItems) && navItems.length > 0 && 'section' in navItems[0] ? (
              // Render sectioned navigation for public site
              (navItems as NavSection[]).map((section, sectionIndex) => (
                <div key={section.section}>
                  {sectionIndex > 0 && <div className="border-t border-gray-200/50 my-4" />}
                  
                  <div className="mb-3">
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider px-2 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-brand-trophy-gold rounded-full mr-2"></span>
                      {section.section}
                    </h3>
                    
                     <div className="space-y-1">
                       {section.items.map((item) => (
                         item.external ? (
                           <a
                             key={item.name}
                             href={item.path}
                             target={item.path.startsWith('tel:') ? '_self' : '_blank'}
                             rel={item.path.startsWith('tel:') ? undefined : 'noopener noreferrer'}
                             className="block px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm relative overflow-hidden group text-gray-700 hover:bg-gradient-to-r hover:from-yellow-50/80 hover:to-brand-trophy-gold/5 hover:text-brand-trophy-gold hover:translate-x-2 hover:shadow-sm"
                             onClick={onClose}
                           >
                             <span className="relative z-10">{item.name}</span>
                             <div className="absolute inset-0 bg-gradient-to-r from-transparent to-brand-trophy-gold/5 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                           </a>
                         ) : (
                           <Link 
                             key={item.name}
                             to={item.path} 
                             className={`
                               block px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm relative overflow-hidden group
                               ${currentPath === item.path 
                                 ? 'bg-gradient-to-r from-brand-trophy-gold/15 to-yellow-100/80 text-brand-trophy-gold border-l-4 border-brand-trophy-gold shadow-md' 
                                 : 'text-gray-700 hover:bg-gradient-to-r hover:from-yellow-50/80 hover:to-brand-trophy-gold/5 hover:text-brand-trophy-gold hover:translate-x-2 hover:shadow-sm'
                               }
                             `}
                             onClick={onClose}
                           >
                             <span className="relative z-10">{item.name}</span>
                             {currentPath !== item.path && (
                               <div className="absolute inset-0 bg-gradient-to-r from-transparent to-brand-trophy-gold/5 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                             )}
                           </Link>
                         )
                       ))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Render flat navigation for admin
              (navItems as NavItem[]).map((item) => (
                <Link 
                  key={item.name}
                  to={item.path} 
                  className={`
                    px-4 py-3 rounded-lg font-medium transition-all duration-200
                    ${currentPath === item.path 
                      ? 'bg-yellow-100 text-brand-trophy-gold' 
                      : 'text-gray-700 hover:bg-yellow-50 hover:text-brand-trophy-gold'
                    }
                  `}
                  onClick={onClose}
                >
                  {item.name}
                </Link>
              ))
            )}
            
            {isPublicSite ? (
              <>
                <div className="border-t border-gray-200/50 my-6" />
                <div className="pt-2 space-y-3">
                  {/* Call Us 24/7 Button */}
                  <Button 
                    onClick={() => {
                      window.open('tel:+12523022003', '_self');
                      onClose();
                    }}
                    className="w-full bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white font-bold py-4 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center space-x-3 hover:shadow-xl hover:scale-[1.02]"
                    aria-label="Call Carolina Trophy Roofs 24/7 Emergency Line at (252) 302-2003 - Mobile"
                  >
                    <Phone className="h-5 w-5" />
                    <span>Call Us 24/7</span>
                  </Button>

                  {/* INSTANT Estimate Button */}
                  <Button 
                    onClick={() => {
                      window.open('https://carolinatrophyroofs.sky-quote.com/Instant-Estimate', '_blank');
                      onClose();
                    }}
                    className="w-full bg-gradient-to-r from-brand-trophy-gold via-yellow-400 to-brand-trophy-gold hover:from-yellow-400 hover:via-brand-trophy-gold hover:to-yellow-400 text-brand-charcoal-gray font-bold py-4 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] relative overflow-hidden group"
                    aria-label="Get instant roofing estimate from Carolina Trophy Roofs - Free online pricing tool - Mobile"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">⚡ INSTANT Estimate!</span>
                  </Button>

                  {/* Contact Us Button */}
                  <Button 
                    onClick={() => {
                      navigate('/contact');
                      onClose();
                    }}
                    className="w-full bg-white border-2 border-brand-charcoal-gray text-brand-charcoal-gray hover:bg-brand-charcoal-gray hover:text-white font-bold py-4 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                    aria-label="Contact Carolina Trophy Roofs - Get roofing help and schedule services - Mobile"
                  >
                    Contact Us!
                  </Button>

                  {/* Apply for Financing Button */}
                  <Button 
                    onClick={() => {
                      navigate('/financing');
                      onClose();
                    }}
                    className="w-full bg-gradient-to-r from-brand-carolina-blue to-blue-600 hover:from-blue-600 hover:to-brand-carolina-blue text-white font-bold py-4 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                    aria-label="Apply for roofing financing with Carolina Trophy Roofs - Mobile"
                  >
                    Apply for Financing
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="border-t border-gray-200 my-4" />
                <Button 
                  variant="outline" 
                  onClick={() => {
                    handleLogout();
                    onClose();
                  }}
                  className="w-full border-2 border-red-200 text-red-600 hover:bg-red-50 font-medium py-3 rounded-lg transition-all duration-200"
                >
                  Logout
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};